import React, { useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useToast } from '../contexts/ToastProvider';
import { CheckCircleIcon, SendIcon, RefreshCwIcon, CopyIcon, ExternalLinkIcon, AlertCircleIcon, PowerIcon, EyeIcon, LinkIcon, InfoIcon, CheckIcon } from 'lucide-react';
export function PublishExercise() {
  const {
    t
  } = useOrganization();
  const {
    showToast
  } = useToast();
  const [isPublished, setIsPublished] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [activationToken] = useState('act_kmg_2025_ex_emo_uk_10_v1_a1b2c3d4');
  const [exerciseId] = useState('EX-EMO-UK-2025-10-v1');
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  // Mock ancillary configuration
  const [hasAncillary] = useState(true);
  const [ancillaryMode] = useState<'internal' | 'external'>('external');
  const [ancillaryPosition] = useState<'prepend' | 'append'>('append');
  const [externalSurveyUrl] = useState('https://survey.qualtrics.com/jfe/form/SV_abc123');
  const [externalIdParameter] = useState('participant_id');
  const [externalReturnUrl] = useState('https://example.com/thank-you');
  // Generate URLs
  const directUrl = `https://runner.agilebrain.io/exercise/${exerciseId}?token=${activationToken}`;
  const embedCode = `<iframe 
  src="${directUrl}" 
  width="100%" 
  height="800" 
  frameborder="0" 
  allow="fullscreen"
  title="AgileBrain Assessment">
</iframe>`;
  const handlePublish = () => {
    setIsPublished(true);
    showToast('Exercise published successfully', 'success');
  };
  const handleRegenerateToken = () => {
    showToast('Activation token regenerated', 'success');
  };
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(label);
    showToast(`${label} copied to clipboard`, 'success');
    setTimeout(() => setCopiedItem(null), 2000);
  };
  const handleToggleActive = () => {
    setIsActive(!isActive);
    showToast(isActive ? 'Exercise deactivated' : 'Exercise activated', 'success');
  };
  return <div className="min-h-full bg-gray-50" data-id="element-2114">
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm" data-id="element-2115">
        <div className="max-w-7xl mx-auto px-8 py-5" data-id="element-2116">
          <div className="flex items-center justify-between" data-id="element-2117">
            <div className="flex items-center gap-4" data-id="element-2118">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1FrxAsdV23omiQNLw7zxz9/Sprinklenet_AI_Square_800px_Robot_Avatar.jpg" alt="Sprinklenet Robot Avatar" className="w-12 h-12 rounded-xl shadow-sm object-cover" data-id="element-2119" />
              <div data-id="element-2120">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-2121">
                  {t.publishExercise}
                </h1>
                <p className="text-sm text-gray-600 mt-1" data-id="element-2122">
                  {t.publishExerciseSubtitle}
                </p>
              </div>
            </div>
            {isPublished && <div className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold ${isActive ? 'bg-green-50 text-green-700 border-2 border-green-200' : 'bg-gray-100 text-gray-600 border-2 border-gray-300'}`} data-id="element-2123">
                <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} data-id="element-2124" />
                {isActive ? 'Active' : 'Inactive'}
              </div>}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-8 py-8" data-id="element-2125">
        <div className="max-w-4xl space-y-6" data-id="element-2126">
          {!isPublished ? <>
              {/* Info Banner */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-sm" data-id="element-2127">
                <div className="flex items-start gap-4" data-id="element-2128">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2129">
                    <AlertCircleIcon className="w-6 h-6 text-white" data-id="element-2130" />
                  </div>
                  <div className="flex-1" data-id="element-2131">
                    <h3 className="text-lg font-bold text-blue-900 mb-2" data-id="element-2132">
                      Publishing creates your exercise's embed code and
                      activation link
                    </h3>
                    <p className="text-sm text-blue-800 leading-relaxed" data-id="element-2133">
                      Once published, you'll receive a unique URL and iframe
                      code that clients can use to integrate the assessment into
                      their website or platform. The exercise can be activated
                      or deactivated at any time.
                    </p>
                  </div>
                </div>
              </div>
              {/* Exercise Summary */}
              <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6" data-id="element-2134">
                <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2" data-id="element-2135">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-2136">
                    <InfoIcon className="w-5 h-5 text-purple-600" data-id="element-2137" />
                  </div>
                  {t.exerciseSummary}
                </h2>
                <div className="space-y-4" data-id="element-2138">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100" data-id="element-2139">
                    <span className="text-sm font-semibold text-gray-600" data-id="element-2140">
                      {t.exerciseId}
                    </span>
                    <span className="text-sm font-mono font-medium text-gray-900 bg-gray-50 px-3 py-1.5 rounded-lg" data-id="element-2141">
                      EX-EMO-UK-2025-10-v1
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100" data-id="element-2142">
                    <span className="text-sm font-semibold text-gray-600" data-id="element-2143">
                      {t.title}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-2144">
                      Emotional State Assessment
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3" data-id="element-2145">
                    <span className="text-sm font-semibold text-gray-600" data-id="element-2146">
                      {t.locales}
                    </span>
                    <div className="flex gap-2" data-id="element-2147">
                      <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full" data-id="element-2148">
                        EN
                      </span>
                      <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full" data-id="element-2149">
                        UK
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Ancillary Configuration Summary */}
              {hasAncillary && <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6" data-id="element-2150">
                  <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2" data-id="element-2151">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-2152">
                      <LinkIcon className="w-5 h-5 text-indigo-600" data-id="element-2153" />
                    </div>
                    Ancillary Survey Configuration
                  </h2>
                  <div className="space-y-4" data-id="element-2154">
                    <div className="flex items-start gap-3 p-4 bg-indigo-50 rounded-lg border border-indigo-200" data-id="element-2155">
                      <div className="w-2 h-2 rounded-full bg-indigo-600 mt-2 flex-shrink-0" data-id="element-2156" />
                      <div className="flex-1" data-id="element-2157">
                        <div className="text-sm font-semibold text-gray-900 mb-1" data-id="element-2158">
                          {ancillaryMode === 'internal' ? 'Internal Survey' : 'External Survey Link'}
                        </div>
                        <div className="text-xs text-gray-600" data-id="element-2159">
                          {ancillaryMode === 'internal' ? 'Built-in survey (PHQ-9, GAD-7, or custom)' : 'Redirects to external survey platform'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200" data-id="element-2160">
                      <div className="w-2 h-2 rounded-full bg-purple-600 mt-2 flex-shrink-0" data-id="element-2161" />
                      <div className="flex-1" data-id="element-2162">
                        <div className="text-sm font-semibold text-gray-900 mb-1" data-id="element-2163">
                          Position:{' '}
                          {ancillaryPosition === 'prepend' ? 'Before' : 'After'}{' '}
                          Image Assessment
                        </div>
                        <div className="text-xs text-gray-600" data-id="element-2164">
                          {ancillaryPosition === 'prepend' ? 'Survey → Image Assessment' : 'Image Assessment → Survey'}
                        </div>
                      </div>
                    </div>
                    {ancillaryMode === 'external' && <>
                        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-2165">
                          <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 flex-shrink-0" data-id="element-2166" />
                          <div className="flex-1" data-id="element-2167">
                            <div className="text-sm font-semibold text-gray-900 mb-2" data-id="element-2168">
                              External URL
                            </div>
                            <code className="text-xs text-gray-700 block break-all bg-white p-3 rounded-lg border border-blue-200" data-id="element-2169">
                              {externalSurveyUrl}
                            </code>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200" data-id="element-2170">
                          <div className="w-2 h-2 rounded-full bg-green-600 mt-2 flex-shrink-0" data-id="element-2171" />
                          <div className="flex-1" data-id="element-2172">
                            <div className="text-sm font-semibold text-gray-900 mb-1" data-id="element-2173">
                              ID Parameter:{' '}
                              <code className="text-purple-600" data-id="element-2174">
                                {externalIdParameter}
                              </code>
                            </div>
                            <div className="text-xs text-gray-600" data-id="element-2175">
                              Participant IDs will be passed via this URL
                              parameter
                            </div>
                          </div>
                        </div>
                      </>}
                  </div>
                </div>}
              <button onClick={handlePublish} className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-3 text-lg" data-id="element-2176">
                <SendIcon className="w-6 h-6" data-id="element-2177" />
                {t.publishExercise}
              </button>
            </> : <div className="space-y-6" data-id="element-2178">
              {/* Success Banner */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 shadow-sm" data-id="element-2179">
                <div className="flex items-start gap-4" data-id="element-2180">
                  <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-2181">
                    <CheckCircleIcon className="w-7 h-7 text-white" data-id="element-2182" />
                  </div>
                  <div className="flex-1" data-id="element-2183">
                    <h2 className="text-xl font-bold text-green-900 mb-2" data-id="element-2184">
                      {t.publishedSuccess}
                    </h2>
                    <p className="text-sm text-green-800 leading-relaxed" data-id="element-2185">
                      {t.publishedDesc}
                    </p>
                  </div>
                </div>
              </div>
              {/* Status Control */}
              <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6" data-id="element-2186">
                <div className="flex items-center justify-between" data-id="element-2187">
                  <div className="flex items-start gap-4" data-id="element-2188">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2189">
                      <PowerIcon className="w-6 h-6 text-gray-600" data-id="element-2190" />
                    </div>
                    <div data-id="element-2191">
                      <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2192">
                        Exercise Status
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed max-w-xl" data-id="element-2193">
                        {isActive ? 'Exercise is currently active and accessible via the embed code and URL below.' : 'Exercise is currently inactive. Activate it to make it accessible to participants.'}
                      </p>
                    </div>
                  </div>
                  <button onClick={handleToggleActive} className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-sm hover:shadow-md ${isActive ? 'bg-red-50 text-red-700 hover:bg-red-100 border-2 border-red-200' : 'bg-green-50 text-green-700 hover:bg-green-100 border-2 border-green-200'}`} data-id="element-2194">
                    <PowerIcon className="w-5 h-5" data-id="element-2195" />
                    {isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
              {/* Access Information */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 shadow-sm" data-id="element-2196">
                <div className="flex items-start gap-3" data-id="element-2197">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2198">
                    <EyeIcon className="w-5 h-5 text-white" data-id="element-2199" />
                  </div>
                  <div className="flex-1" data-id="element-2200">
                    <h4 className="text-sm font-bold text-blue-900 mb-1" data-id="element-2201">
                      How Participants Access This Exercise
                    </h4>
                    <p className="text-sm text-blue-800 leading-relaxed" data-id="element-2202">
                      Participants can ONLY access this exercise through the
                      direct URL or embed code shown below. There is no other
                      way to access the assessment. Share these with your
                      clients for integration.
                    </p>
                  </div>
                </div>
              </div>
              {/* Direct URL */}
              <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6" data-id="element-2203">
                <div className="flex items-center justify-between mb-4" data-id="element-2204">
                  <div data-id="element-2205">
                    <label className="text-base font-bold text-gray-900 block mb-1" data-id="element-2206">
                      Direct Exercise URL
                    </label>
                    <p className="text-xs text-gray-600" data-id="element-2207">
                      Share this URL directly or use it in the embed code below
                    </p>
                  </div>
                  <button onClick={() => window.open(directUrl, '_blank')} className="text-sm text-purple-600 hover:text-purple-700 font-semibold flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-purple-50 transition-colors" data-id="element-2208">
                    <ExternalLinkIcon className="w-4 h-4" data-id="element-2209" />
                    Open
                  </button>
                </div>
                <div className="flex gap-3" data-id="element-2210">
                  <code className="flex-1 bg-gray-50 p-4 rounded-lg border-2 border-gray-200 text-sm font-mono break-all" data-id="element-2211">
                    {directUrl}
                  </code>
                  <button onClick={() => handleCopy(directUrl, 'URL')} className={`px-5 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 border-2 ${copiedItem === 'URL' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200'}`} data-id="element-2212">
                    {copiedItem === 'URL' ? <>
                        <CheckIcon className="w-4 h-4" data-id="element-2213" />
                        Copied
                      </> : <>
                        <CopyIcon className="w-4 h-4" data-id="element-2214" />
                        Copy
                      </>}
                  </button>
                </div>
              </div>
              {/* Embed Code */}
              <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6" data-id="element-2215">
                <div className="mb-4" data-id="element-2216">
                  <label className="text-base font-bold text-gray-900 block mb-1" data-id="element-2217">
                    {t.embedCode}
                  </label>
                  <p className="text-xs text-gray-600" data-id="element-2218">
                    Copy this iframe code and paste it into your website's HTML
                  </p>
                </div>
                <div className="flex gap-3" data-id="element-2219">
                  <pre className="flex-1 bg-gray-50 p-4 rounded-lg border-2 border-gray-200 text-xs font-mono overflow-x-auto" data-id="element-2220">
                    {embedCode}
                  </pre>
                  <button onClick={() => handleCopy(embedCode, 'Embed code')} className={`px-5 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 border-2 self-start ${copiedItem === 'Embed code' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200'}`} data-id="element-2221">
                    {copiedItem === 'Embed code' ? <>
                        <CheckIcon className="w-4 h-4" data-id="element-2222" />
                        Copied
                      </> : <>
                        <CopyIcon className="w-4 h-4" data-id="element-2223" />
                        Copy
                      </>}
                  </button>
                </div>
              </div>
              {/* Activation Token */}
              <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6" data-id="element-2224">
                <div className="flex items-center justify-between mb-4" data-id="element-2225">
                  <div data-id="element-2226">
                    <label className="text-base font-bold text-gray-900 block mb-1" data-id="element-2227">
                      {t.activationToken}
                    </label>
                    <p className="text-xs text-gray-600" data-id="element-2228">
                      This token is embedded in the URL and authenticates access
                      to the exercise
                    </p>
                  </div>
                  <button onClick={handleRegenerateToken} className="text-sm text-purple-600 hover:text-purple-700 font-semibold flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-purple-50 transition-colors" data-id="element-2229">
                    <RefreshCwIcon className="w-4 h-4" data-id="element-2230" />
                    {t.regenerateToken}
                  </button>
                </div>
                <div className="flex gap-3" data-id="element-2231">
                  <code className="flex-1 bg-gray-50 p-4 rounded-lg border-2 border-gray-200 text-sm font-mono" data-id="element-2232">
                    {activationToken}
                  </code>
                  <button onClick={() => handleCopy(activationToken, 'Token')} className={`px-5 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 border-2 ${copiedItem === 'Token' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200'}`} data-id="element-2233">
                    {copiedItem === 'Token' ? <>
                        <CheckIcon className="w-4 h-4" data-id="element-2234" />
                        Copied
                      </> : <>
                        <CopyIcon className="w-4 h-4" data-id="element-2235" />
                        Copy
                      </>}
                  </button>
                </div>
              </div>
              {/* External Survey Integration Info */}
              {hasAncillary && ancillaryMode === 'external' && <div className="bg-white rounded-xl shadow-sm border-2 border-indigo-200 p-6" data-id="element-2236">
                  <div className="flex items-center gap-3 mb-5" data-id="element-2237">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-2238">
                      <LinkIcon className="w-6 h-6 text-indigo-600" data-id="element-2239" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-2240">
                      External Survey Integration
                    </h3>
                  </div>
                  <div className="space-y-5" data-id="element-2241">
                    {/* Flow Explanation */}
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-4" data-id="element-2242">
                      <div className="flex items-start gap-3" data-id="element-2243">
                        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2244">
                          <InfoIcon className="w-5 h-5 text-white" data-id="element-2245" />
                        </div>
                        <div className="flex-1" data-id="element-2246">
                          <h4 className="text-sm font-bold text-indigo-900 mb-1" data-id="element-2247">
                            Participant Flow
                          </h4>
                          <p className="text-sm text-indigo-800 leading-relaxed" data-id="element-2248">
                            {ancillaryPosition === 'prepend' ? <>
                                Participants will first be redirected to your
                                external survey, then return to complete the
                                image assessment.
                              </> : <>
                                After completing the image assessment,
                                participants will be automatically redirected to
                                your external survey.
                              </>}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Generated Redirect URL */}
                    <div data-id="element-2249">
                      <label className="block text-sm font-bold text-gray-900 mb-3" data-id="element-2250">
                        Generated Redirect URL (Example)
                      </label>
                      <code className="block text-xs bg-gray-50 p-4 rounded-lg border-2 border-gray-200 break-all font-mono" data-id="element-2251">
                        {externalSurveyUrl}
                        {externalSurveyUrl.includes('?') ? '&' : '?'}
                        {externalIdParameter}=EXT-UKR-000123
                      </code>
                      <p className="text-xs text-gray-600 mt-2" data-id="element-2252">
                        Each participant will receive a unique URL with their ID
                        automatically appended
                      </p>
                    </div>
                    {/* Data Integration Instructions */}
                    <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-5" data-id="element-2253">
                      <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-2254">
                        <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-2255">
                          <InfoIcon className="w-4 h-4 text-purple-600" data-id="element-2256" />
                        </div>
                        Data Integration Instructions
                      </h4>
                      <ol className="text-sm text-gray-700 space-y-3 list-decimal list-inside" data-id="element-2257">
                        <li className="leading-relaxed" data-id="element-2258">
                          Ensure your external survey captures the "
                          {externalIdParameter}" parameter from the URL
                        </li>
                        <li className="leading-relaxed" data-id="element-2259">
                          Store this ID alongside survey responses in your
                          survey platform
                        </li>
                        <li className="leading-relaxed" data-id="element-2260">
                          Export both datasets (image assessment results from
                          AgileBrain and survey responses from your platform)
                        </li>
                        <li className="leading-relaxed" data-id="element-2261">
                          Match records using the participant ID to combine data
                          for analysis
                        </li>
                        <li className="leading-relaxed" data-id="element-2262">
                          Image assessment results are available via the Results
                          page or API
                        </li>
                      </ol>
                    </div>
                    {/* Return URL Info */}
                    {externalReturnUrl && <div data-id="element-2263">
                        <label className="block text-sm font-bold text-gray-900 mb-3" data-id="element-2264">
                          Return URL After Survey
                        </label>
                        <code className="block text-xs bg-gray-50 p-4 rounded-lg border-2 border-gray-200 break-all font-mono" data-id="element-2265">
                          {externalReturnUrl}
                        </code>
                        <p className="text-xs text-gray-600 mt-2" data-id="element-2266">
                          Configure your external survey to redirect
                          participants here upon completion
                        </p>
                      </div>}
                  </div>
                </div>}
              {/* Integration Instructions */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-6 shadow-sm" data-id="element-2267">
                <h3 className="text-base font-bold text-purple-900 mb-4 flex items-center gap-2" data-id="element-2268">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center" data-id="element-2269">
                    <InfoIcon className="w-5 h-5 text-white" data-id="element-2270" />
                  </div>
                  Integration Instructions
                </h3>
                <ol className="text-sm text-purple-900 space-y-3 list-decimal list-inside leading-relaxed" data-id="element-2271">
                  <li data-id="element-2272">
                    Copy the embed code above and paste it into your website's
                    HTML
                  </li>
                  <li data-id="element-2273">
                    Adjust the iframe width and height as needed for your layout
                  </li>
                  <li data-id="element-2274">
                    The activation token is included in the URL and
                    authenticates access
                  </li>
                  {hasAncillary && ancillaryMode === 'external' && <li data-id="element-2275">
                      External survey integration is configured - participants
                      will be redirected automatically with their unique ID
                    </li>}
                  <li data-id="element-2276">
                    Results will be automatically delivered to your configured
                    Knowledge Spaces chatbot
                  </li>
                  <li data-id="element-2277">
                    Use the status toggle above to activate or deactivate the
                    exercise at any time
                  </li>
                </ol>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}